import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, MessageSquare, LogIn, Copy, Check, ExternalLink, Code } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WidgetsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [copied, setCopied] = useState({});
  const [settings, setSettings] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState({
    availability: {
      primaryColor: '#06b6d4',
      buttonText: 'Check Availability',
      title: 'Check Our Availability',
      theme: 'dark'
    },
    contact: {
      primaryColor: '#06b6d4',
      buttonText: 'Send Message',
      title: 'Contact Us',
      theme: 'dark'
    },
    login: {
      primaryColor: '#06b6d4',
      buttonText: 'Access Portal',
      title: 'Client Portal Login',
      theme: 'dark'
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      if (response.data.primary_color) {
        setWidgetConfig(prev => ({
          availability: { ...prev.availability, primaryColor: response.data.primary_color },
          contact: { ...prev.contact, primaryColor: response.data.primary_color },
          login: { ...prev.login, primaryColor: response.data.primary_color }
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };

  const generateAvailabilityCode = () => {
    const theme = widgetConfig.availability.theme;
    return `<!-- DJ CRM Availability Widget (Auto-resizing) -->
<iframe 
  id="djcrm-availability-widget"
  src="${BACKEND_URL}/widget/availability/${user?.id}?theme=${theme}"
  width="100%" 
  frameborder="0"
  style="border-radius: 12px; max-width: 500px; min-height: 400px;${theme === 'light' ? ' background: #ffffff;' : ''}"
  title="Check Availability"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data && e.data.type === 'djcrm-resize' && e.data.widget === 'availability') {
    document.getElementById('djcrm-availability-widget').style.height = e.data.height + 'px';
  }
});
</script>`;
  };

  const generateContactCode = () => {
    const theme = widgetConfig.contact.theme;
    return `<!-- DJ CRM Contact Widget (Auto-resizing) -->
<iframe 
  id="djcrm-contact-widget"
  src="${BACKEND_URL}/widget/contact/${user?.id}?theme=${theme}"
  width="100%" 
  frameborder="0"
  style="border-radius: 12px; max-width: 500px; min-height: 500px;${theme === 'light' ? ' background: #ffffff;' : ''}"
  title="Contact Form"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data && e.data.type === 'djcrm-resize' && e.data.widget === 'contact') {
    document.getElementById('djcrm-contact-widget').style.height = e.data.height + 'px';
  }
});
</script>`;
  };

  const generateLoginCode = () => {
    const theme = widgetConfig.login.theme;
    return `<!-- DJ CRM Client Portal Widget (Auto-resizing) -->
<iframe 
  id="djcrm-login-widget"
  src="${BACKEND_URL}/widget/login/${user?.id}?theme=${theme}"
  width="100%" 
  frameborder="0"
  style="border-radius: 12px; max-width: 500px; min-height: 400px;${theme === 'light' ? ' background: #ffffff;' : ''}"
  title="Client Portal Login"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data && e.data.type === 'djcrm-resize' && e.data.widget === 'login') {
    document.getElementById('djcrm-login-widget').style.height = e.data.height + 'px';
  }
});
</script>`;
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="widgets-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="widgets-heading">Website Widgets</h1>
            <p className="text-muted-foreground">Get embeddable widgets for your website to let clients check availability, contact you, or login to their portal</p>
          </div>

          <Tabs defaultValue="availability" className="space-y-6">
            <TabsList className="glass-card border-glass-border">
              <TabsTrigger value="availability" data-testid="tab-availability">
                <Calendar className="w-4 h-4 mr-2" />
                Availability
              </TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact">
                <MessageSquare className="w-4 h-4 mr-2" />
                Contact Form
              </TabsTrigger>
              <TabsTrigger value="login" data-testid="tab-login">
                <LogIn className="w-4 h-4 mr-2" />
                Client Login
              </TabsTrigger>
            </TabsList>

            {/* Availability Widget Tab */}
            <TabsContent value="availability">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <Calendar className="w-6 h-6 text-neon-blue" />
                      Availability Checker
                    </CardTitle>
                    <CardDescription>
                      Let visitors check if you're available for their event date
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Theme Toggle */}
                    <div className="space-y-2">
                      <Label>Widget Theme</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={widgetConfig.availability.theme === 'dark' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setWidgetConfig(prev => ({
                            ...prev,
                            availability: { ...prev.availability, theme: 'dark' }
                          }))}
                          className={widgetConfig.availability.theme === 'dark' ? 'bg-neon-blue' : 'border-glass-border'}
                        >
                          Dark
                        </Button>
                        <Button
                          variant={widgetConfig.availability.theme === 'light' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setWidgetConfig(prev => ({
                            ...prev,
                            availability: { ...prev.availability, theme: 'light' }
                          }))}
                          className={widgetConfig.availability.theme === 'light' ? 'bg-white text-black' : 'border-glass-border'}
                        >
                          Light
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Widget Preview</Label>
                      <div className={`rounded-xl p-6 border ${
                        widgetConfig.availability.theme === 'light' 
                          ? 'bg-white border-gray-200' 
                          : 'bg-white/5 border-glass-border'
                      }`}>
                        <div className="text-center space-y-4">
                          <h3 className={`font-heading text-xl font-semibold ${
                            widgetConfig.availability.theme === 'light' ? 'text-gray-900' : ''
                          }`}>{widgetConfig.availability.title}</h3>
                          <p className={`text-sm ${
                            widgetConfig.availability.theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
                          }`}>Select a date to check our availability</p>
                          <div className="flex justify-center">
                            <input 
                              type="date" 
                              className={`border rounded-lg px-4 py-2 ${
                                widgetConfig.availability.theme === 'light'
                                  ? 'bg-gray-50 border-gray-300 text-gray-900'
                                  : 'bg-muted/20 border-glass-border text-foreground'
                              }`}
                              disabled
                            />
                          </div>
                          <Button 
                            className="text-black font-semibold"
                            style={{ backgroundColor: widgetConfig.availability.primaryColor }}
                            disabled
                          >
                            {widgetConfig.availability.buttonText}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Embed Code</Label>
                      <div className="relative">
                        <pre className="bg-muted/20 border border-glass-border rounded-lg p-4 text-sm overflow-x-auto font-mono text-muted-foreground">
                          {generateAvailabilityCode()}
                        </pre>
                        <Button
                          size="sm"
                          onClick={() => copyToClipboard(generateAvailabilityCode(), 'availability')}
                          className="absolute top-2 right-2 bg-neon-blue/20 hover:bg-neon-blue/30 text-neon-blue"
                          data-testid="copy-availability-code"
                        >
                          {copied.availability ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 border-glass-border"
                        onClick={() => window.open(`${BACKEND_URL}/widget/availability/${user?.id}`, '_blank')}
                        data-testid="preview-availability"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview Widget
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">How It Works</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-neon-blue/20 flex items-center justify-center text-neon-blue font-bold">1</div>
                        <div>
                          <h4 className="font-semibold">Visitor selects a date</h4>
                          <p className="text-sm text-muted-foreground">They pick their preferred event date from the calendar</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-neon-blue/20 flex items-center justify-center text-neon-blue font-bold">2</div>
                        <div>
                          <h4 className="font-semibold">System checks your bookings</h4>
                          <p className="text-sm text-muted-foreground">The widget checks against your existing bookings</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-neon-blue/20 flex items-center justify-center text-neon-blue font-bold">3</div>
                        <div>
                          <h4 className="font-semibold">Shows availability status</h4>
                          <p className="text-sm text-muted-foreground">If available, they can proceed to contact you</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-neon-blue/10 border border-neon-blue/30">
                      <p className="text-sm">
                        <span className="font-semibold text-neon-blue">Pro Tip:</span> Place this widget on your homepage or booking page to reduce back-and-forth inquiries about availability.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Contact Form Widget Tab */}
            <TabsContent value="contact">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <MessageSquare className="w-6 h-6 text-neon-purple" />
                      Contact Form
                    </CardTitle>
                    <CardDescription>
                      Let potential clients send you inquiries directly from your website
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Theme Toggle */}
                    <div className="space-y-2">
                      <Label>Widget Theme</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={widgetConfig.contact.theme === 'dark' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setWidgetConfig(prev => ({
                            ...prev,
                            contact: { ...prev.contact, theme: 'dark' }
                          }))}
                          className={widgetConfig.contact.theme === 'dark' ? 'bg-neon-purple' : 'border-glass-border'}
                        >
                          Dark
                        </Button>
                        <Button
                          variant={widgetConfig.contact.theme === 'light' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setWidgetConfig(prev => ({
                            ...prev,
                            contact: { ...prev.contact, theme: 'light' }
                          }))}
                          className={widgetConfig.contact.theme === 'light' ? 'bg-white text-black' : 'border-glass-border'}
                        >
                          Light
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Widget Preview</Label>
                      <div className={`rounded-xl p-6 border ${
                        widgetConfig.contact.theme === 'light' 
                          ? 'bg-white border-gray-200' 
                          : 'bg-white/5 border-glass-border'
                      }`}>
                        <div className="space-y-4">
                          <h3 className={`font-heading text-xl font-semibold text-center ${
                            widgetConfig.contact.theme === 'light' ? 'text-gray-900' : ''
                          }`}>{widgetConfig.contact.title}</h3>
                          <div className="space-y-3">
                            <Input placeholder="Your Name" disabled className={
                              widgetConfig.contact.theme === 'light'
                                ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500'
                                : 'bg-muted/20 border-glass-border'
                            } />
                            <Input placeholder="Email Address" disabled className={
                              widgetConfig.contact.theme === 'light'
                                ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500'
                                : 'bg-muted/20 border-glass-border'
                            } />
                            <Input placeholder="Phone Number" disabled className={
                              widgetConfig.contact.theme === 'light'
                                ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500'
                                : 'bg-muted/20 border-glass-border'
                            } />
                            <Input type="date" disabled className={
                              widgetConfig.contact.theme === 'light'
                                ? 'bg-gray-50 border-gray-300 text-gray-900'
                                : 'bg-muted/20 border-glass-border'
                            } />
                            <textarea 
                              placeholder="Tell us about your event..."
                              disabled
                              className={`w-full min-h-[60px] border rounded-lg p-3 ${
                                widgetConfig.contact.theme === 'light'
                                  ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500'
                                  : 'bg-muted/20 border-glass-border text-muted-foreground'
                              }`}
                            />
                          </div>
                          <Button 
                            className="w-full text-black font-semibold"
                            style={{ backgroundColor: widgetConfig.contact.primaryColor }}
                            disabled
                          >
                            {widgetConfig.contact.buttonText}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Embed Code</Label>
                      <div className="relative">
                        <pre className="bg-muted/20 border border-glass-border rounded-lg p-4 text-sm overflow-x-auto font-mono text-muted-foreground">
                          {generateContactCode()}
                        </pre>
                        <Button
                          size="sm"
                          onClick={() => copyToClipboard(generateContactCode(), 'contact')}
                          className="absolute top-2 right-2 bg-neon-purple/20 hover:bg-neon-purple/30 text-neon-purple"
                          data-testid="copy-contact-code"
                        >
                          {copied.contact ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 border-glass-border"
                        onClick={() => window.open(`${BACKEND_URL}/widget/contact/${user?.id}`, '_blank')}
                        data-testid="preview-contact"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview Widget
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Lead Generation</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-neon-purple/20 flex items-center justify-center text-neon-purple font-bold">1</div>
                        <div>
                          <h4 className="font-semibold">Visitor fills out the form</h4>
                          <p className="text-sm text-muted-foreground">They provide their contact info and event details</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-neon-purple/20 flex items-center justify-center text-neon-purple font-bold">2</div>
                        <div>
                          <h4 className="font-semibold">Lead is created automatically</h4>
                          <p className="text-sm text-muted-foreground">A new lead appears in your Leads dashboard</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-neon-purple/20 flex items-center justify-center text-neon-purple font-bold">3</div>
                        <div>
                          <h4 className="font-semibold">Follow up and convert</h4>
                          <p className="text-sm text-muted-foreground">Contact the lead and convert them to a booking</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-neon-purple/10 border border-neon-purple/30">
                      <p className="text-sm">
                        <span className="font-semibold text-neon-purple">Note:</span> All submissions create leads in your CRM automatically. You'll see them in the Leads section.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Client Login Widget Tab */}
            <TabsContent value="login">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <LogIn className="w-6 h-6 text-green-500" />
                      Client Portal Login
                    </CardTitle>
                    <CardDescription>
                      Let your booked clients access their event portal
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Theme Toggle */}
                    <div className="space-y-2">
                      <Label>Widget Theme</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={widgetConfig.login.theme === 'dark' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setWidgetConfig(prev => ({
                            ...prev,
                            login: { ...prev.login, theme: 'dark' }
                          }))}
                          className={widgetConfig.login.theme === 'dark' ? 'bg-green-500' : 'border-glass-border'}
                        >
                          Dark
                        </Button>
                        <Button
                          variant={widgetConfig.login.theme === 'light' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setWidgetConfig(prev => ({
                            ...prev,
                            login: { ...prev.login, theme: 'light' }
                          }))}
                          className={widgetConfig.login.theme === 'light' ? 'bg-white text-black' : 'border-glass-border'}
                        >
                          Light
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Widget Preview</Label>
                      <div className={`rounded-xl p-6 border ${
                        widgetConfig.login.theme === 'light' 
                          ? 'bg-white border-gray-200' 
                          : 'bg-white/5 border-glass-border'
                      }`}>
                        <div className="text-center space-y-4">
                          <h3 className={`font-heading text-xl font-semibold ${
                            widgetConfig.login.theme === 'light' ? 'text-gray-900' : ''
                          }`}>{widgetConfig.login.title}</h3>
                          <p className={`text-sm ${
                            widgetConfig.login.theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
                          }`}>Enter your booking email to access your event details</p>
                          <div className="space-y-3 max-w-xs mx-auto">
                            <Input placeholder="Email Address" disabled className={
                              widgetConfig.login.theme === 'light'
                                ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500'
                                : 'bg-muted/20 border-glass-border'
                            } />
                            <Input placeholder="Booking Code" disabled className={
                              widgetConfig.login.theme === 'light'
                                ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-500'
                                : 'bg-muted/20 border-glass-border'
                            } />
                          </div>
                          <Button 
                            className="text-black font-semibold"
                            style={{ backgroundColor: widgetConfig.login.primaryColor }}
                            disabled
                          >
                            {widgetConfig.login.buttonText}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Embed Code</Label>
                      <div className="relative">
                        <pre className="bg-muted/20 border border-glass-border rounded-lg p-4 text-sm overflow-x-auto font-mono text-muted-foreground">
                          {generateLoginCode()}
                        </pre>
                        <Button
                          size="sm"
                          onClick={() => copyToClipboard(generateLoginCode(), 'login')}
                          className="absolute top-2 right-2 bg-green-500/20 hover:bg-green-500/30 text-green-500"
                          data-testid="copy-login-code"
                        >
                          {copied.login ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 border-glass-border"
                        onClick={() => window.open(`${BACKEND_URL}/widget/login/${user?.id}`, '_blank')}
                        data-testid="preview-login"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview Widget
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card border-glass-border">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">Client Portal Access</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 font-bold">1</div>
                        <div>
                          <h4 className="font-semibold">Client enters their email</h4>
                          <p className="text-sm text-muted-foreground">The email used when they booked with you</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 font-bold">2</div>
                        <div>
                          <h4 className="font-semibold">Enters their booking code</h4>
                          <p className="text-sm text-muted-foreground">The unique code sent to them upon booking</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 font-bold">3</div>
                        <div>
                          <h4 className="font-semibold">Access their portal</h4>
                          <p className="text-sm text-muted-foreground">View contracts, invoices, playlists, and timelines</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                      <p className="text-sm">
                        <span className="font-semibold text-green-500">Tip:</span> Include the portal login widget on a dedicated "Client Area" page on your website for easy access.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Installation Guide */}
          <Card className="glass-card border-glass-border mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Code className="w-5 h-5" />
                Installation Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h4 className="font-semibold">WordPress</h4>
                  <p className="text-sm text-muted-foreground">
                    Add a Custom HTML block and paste the embed code. Works with any page builder.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold">Wix / Squarespace</h4>
                  <p className="text-sm text-muted-foreground">
                    Use the "Embed" or "HTML" element to add the iframe code to your page.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold">Custom Website</h4>
                  <p className="text-sm text-muted-foreground">
                    Simply paste the HTML code anywhere in your website's source code.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
